import { fabric } from 'fabric'
import { BoxDef, fullHeight, fullWidth } from './boxDef'
import { PalletDef } from './palletDef'
import * as utils from './utils'
import * as consts from './pattern-editor-constants'

interface Point {
  x: number
  y: number
}

export class Snap {
  private active: boolean = true
  private canvas: fabric.Canvas
  private boxDef: BoxDef
  private palDef: PalletDef
  private snapPointsX: number[] = []
  private snapPointsY: number[] = []
  private dropZone: fabric.Object = new fabric.Object()
  private linesXPull: fabric.Object[] = []
  private linesYPull: fabric.Object[] = []
  private offsetTop: number

  constructor (canvas: fabric.Canvas, boxDef: BoxDef, palDef: PalletDef, offsetTop: number) {
    this.canvas = canvas
    this.boxDef = boxDef
    this.palDef = palDef
    this.offsetTop = offsetTop
  }

  init () {
    for (let i = 0; i < 100; i++) {
      const lineX = this.makeLineX(0)
      this.linesXPull.push(lineX)
      this.canvas.add(lineX)

      const lineY = this.makeLineY(0)
      this.linesYPull.push(lineY)
      this.canvas.add(lineY)
    }
    this.dropZone = this.makeDropZone()
  }

  clear () {
    this.dropZone.set({ opacity: 0 })
  }

  setActive(active: boolean): void {
    this.active = active
  }

  // This should be called once object starts to move,
  // before 'drawEdges'.
  // Expensive operation
  updatePoints (obj: fabric.Object) {
    this.snapPointsX.length = 0
    this.snapPointsX.push(...this.getSnapPointsX(obj))
    this.snapPointsY.length = 0
    this.snapPointsY.push(...this.getSnapPointsY(obj))
  }

  drawRulers (obj: fabric.Object, precisionMovement: boolean = false) {
    if (obj.name !== 'box') {
      return
    }
    this.clearSnapLines()
    if (!this.active && !precisionMovement) { // snap deactivated, no precision movement
      return
    }

    // get object edges
    const bb = this.getTopLeftPoint(obj)
    const width = this.boxDef.width * utils.projX(obj.angle!) + this.boxDef.height * utils.projY(obj.angle!)
    const height = this.boxDef.height * utils.projX(obj.angle!) + this.boxDef.width * utils.projY(obj.angle!)

    // trimMargin(bb, boxDef)    // TODO: why this?!
    const Vedges: number[] = [bb.x, bb.x + width]
    const Hedges: number[] = [bb.y, bb.y + height]

    // find out if object vertical edges snap to some objects
    // if true, draw vertical line
    const snapsX = this.snapPointsX.filter (x => this.edgeMatch (Vedges, x))
    const snapsXExactMatches = snapsX.filter(x => Vedges.includes(x))
    if (precisionMovement && snapsXExactMatches.length > 0) {
      snapsXExactMatches.forEach (snap => {
        this.getFromPullLineX (snap, true)
      })
    } else {
      snapsX.forEach (snap => {
        this.getFromPullLineX (snap, false)
      })
    }

    // find out if object horizontal edges snap to some objects
    const snapsY = this.snapPointsY.filter (y => this.edgeMatch (Hedges, y))
    const snapsYExactMatches = snapsY.filter(y => Hedges.includes(y))
    if (precisionMovement && snapsYExactMatches.length > 0) {
      snapsYExactMatches.forEach (snap => {
        this.getFromPullLineY (snap, true)
      })
    } else {
      snapsY.forEach (snap => {
        this.getFromPullLineY (snap, false)
      })
    }
    this.canvas.renderAll()
  }

  removeRulers () {
    this.clearSnapLines()
  }

  getTopLeftPoint (box: fabric.Object) {
    const point = (box.angle! % 360) === 0 ? box.aCoords!.tl
      : (box.angle! % 360 === 90) ? box.aCoords!.bl
        : (box.angle! % 360) === 180 ? box.aCoords!.br
          : box.aCoords!.tr
    point.x = Math.floor(point.x + 0.5)
    point.y = Math.floor(point.y + 0.5)
    return point
  }

  dropToSnap(obj: fabric.Object): boolean {
    if (!this.active) {
      return false
    }
    if (this.dropZone.opacity! === 0) {
      return false // no snap zone, just return
    }
    // move object to the drop zone
    console.log('[DEBUG] drop zone', JSON.stringify([this.dropZone.left, this.dropZone.top]))
    obj.set({ left: this.dropZone.left, top: this.dropZone.top })
    obj.setCoords() // important !
    return true
  }

  highlightDropzone (target: fabric.Object): void {
    if (!this.active) {
      return
    }
    const fixedBox = this.nearestBox(target)
    if (!fixedBox) {
      return
    }
    this.dropZone.set('opacity', 0)
    const p1 = fixedBox.getCenterPoint()
    const p2 = target.getCenterPoint()
    const dropPoint = this.getDropPoint(p1, p2, fixedBox.angle!, target.angle!)
    if (!dropPoint || this.isBoxExists(dropPoint)) {
      return
    }
    this.dropZone.set({
      left: dropPoint.x, top: dropPoint.y, opacity: consts.vdzOpacity,
      angle: target.angle
    })
  }

  /* ==================================================================================================================
   *
   *   PRIVATE METHODS
   *
   ===================================================================================================================*/

  /*
  private rotatePoint (point: Point, angle: number, basis: Point): Point {
    if (!basis) {
      basis = { x: 0, y: 0 }
    }
    const angleRad = angle * Math.PI / 180
    const rotated = { x: 0, y: 0 }
    rotated.x = (point.x - basis.x) * Math.cos(angleRad) - (point.y - basis.y) * Math.sin(angleRad) + basis.x
    rotated.y = (point.x - basis.x) * Math.sin(angleRad) + (point.y - basis.y) * Math.cos(angleRad) + basis.y
    return rotated
  }
  */

  private getFromPullLineX (snap: number, exactMatch: boolean): void {
    const line = this.linesXPull.find(p => p.left === 0)
    if (line) {
      const color = exactMatch ? consts.statusGoodColor : consts.badPlaceColor
      line.set({ left: snap, opacity: 1, fill: color, stroke: color})
      this.canvas.bringToFront(line)
    }
  }

  private returnToPullLineX (line: fabric.Object): void {
    line.set({ left: 0 })
  }

  private getFromPullLineY (snap: number, exactMatch: boolean): void {
    const line = this.linesYPull.find(p => p.top === 0)
    if (line) {
      const color = exactMatch ? consts.statusGoodColor : consts.badPlaceColor
      line.set({ top: snap, opacity: 1, fill: color, stroke: color})
      this.canvas.bringToFront(line)
    }
  }

  private returnToPullLineY (line: fabric.Object): void {
    line.set({ top: 0 })
  }

  private isBoxExists(p: Point): boolean {
    return this.canvas.getObjects().some(obj => obj.name === 'box' && obj.left === p.x && obj.top === p.y)
  }

  private getDropPoint (p1: Point, p2: Point, angle1: number, angle2: number): Point | null {
    const vertex = { x: p2.x - p1.x, y: p2.y - p1.y }
    const boxWidth = fullWidth(this.boxDef)
    const boxHeight = fullHeight(this.boxDef)
    const aspectRatio = Math.max(boxWidth, boxHeight) / Math.min(boxWidth, boxHeight)

    // offset - is a value in the range [1, 0, -1] defines direction on a drop box
    const factor = aspectRatio <= 2 ? 0.4 : 1
    const signX = Math.abs(vertex.x) * factor > Math.abs(vertex.y) ? Math.sign(vertex.x) : 0
    const signY = Math.abs(vertex.y) * factor > Math.abs(vertex.x) ? Math.sign(vertex.y) : 0
    if (signX === 0 && signY === 0) {
      return null
    }
    // object 1
    const width1 = angle1 % 180 === 0  ? boxWidth : boxHeight
    const height1 = angle1 % 180 === 0 ? boxHeight : boxWidth
    // object 2
    const width2 = angle2 % 180 === 0 ? boxWidth : boxHeight
    const height2 = angle2 % 180 === 0 ? boxHeight : boxWidth

    // check if the objects are close enough to snap
    if ((signX !== 0 && Math.abs(vertex.x) > (width1 + width2) / 2 + 50)
      || (signY !== 0 && Math.abs(vertex.y) > (height1 + height2) / 2 + 50)) {
      return null
    }
    // align objects by closest side
    const offsetX = signX === 0 ? Math.abs(width1 - width2) / 2 * Math.sign(vertex.x) : 0
    const offsetY = signY === 0 ? Math.abs(height1 - height2) / 2 * Math.sign(vertex.y) : 0

    const dropPoint = {
      x: p1.x + Math.round((width1 + width2) / 2 * signX + offsetX),
      y: p1.y + Math.round((height1 + height2) / 2 * signY + offsetY)
    }
    console.log('[DEBUG] dropPoint', JSON.stringify(dropPoint))
    return dropPoint
  }

  private clearSnapLines () {
    this.linesXPull.filter(p => p.left !== 0).forEach(q => q.set({ left: 0, opacity: 0, fill: consts.badPlaceColor, stroke: consts.badPlaceColor}))
    this.linesYPull.filter(p => p.top !== 0).forEach(q => q.set({ top: 0, opacity: 0, fill: consts.badPlaceColor, stroke: consts.badPlaceColor}))
  }

  private edgeMatch (edges: number[], candidate: number): boolean {
    return edges.some(p => Math.abs(p - candidate) < consts.edgeMatchMargin)
  }

  private getSnapPointsX (exclude: fabric.Object): number[] {
    let bb: any = {}
    let width = 0
    return this.canvas.getObjects()
      .filter(obj => obj.name === 'box' && obj !== exclude)
      .map(box => {
        bb = this.getTopLeftPoint(box)
        width = this.boxDef.width * utils.projX(box.angle!) + this.boxDef.height * utils.projY(box.angle!)
        // return [bb.x - 1, bb.x + width - 1]
        return [bb.x, bb.x + width]
      })
      .flat()
  }

  private getSnapPointsY (exclude: fabric.Object): number[] {
    let bb: any = {}
    let height = 0
    return this.canvas.getObjects()
      .filter(obj => obj.name === 'box' && obj !== exclude)
      .map(box => {
        bb = this.getTopLeftPoint(box)
        height = this.boxDef.height * utils.projX(box.angle!) + this.boxDef.width * utils.projY(box.angle!)
        // return [bb.y - 1, bb.y + height - 1]
        return [bb.y, bb.y + height]
      })
      .flat()
  }

  private makeLineX (x: number): fabric.Line {
    const offsetY = this.palDef.topLeftY + this.offsetTop
    const margin = offsetY + this.palDef.overhangB + this.palDef.overhangT
    return new fabric.Line(
      [x, offsetY, x, margin + this.palDef.height],
      {
        fill: consts.badPlaceColor,
        stroke: consts.badPlaceColor,
        strokeWidth: 3,
        opacity: 0,
        name: 'edge'
      }
    )
  }

  private makeLineY (y: number): fabric.Line {
    const margin = this.palDef.topLeftX + this.palDef.overhangL + this.palDef.overhangR
    return new fabric.Line(
      [this.palDef.topLeftX, y, margin + this.palDef.width, y],
      {
        fill: consts.badPlaceColor,
        stroke: consts.badPlaceColor,
        strokeWidth: 3,
        opacity: 0,
        name: 'edge'
      }
    )
  }

  private nearestBox (box: fabric.Object): (fabric.Object | undefined) {
    const targetCorners = box.getCoords()
    let minDistanceEdge = 99999
    let closestEdge: fabric.Object | undefined
    this.canvas.getObjects().filter(p => p.name === 'box' && p !== box).forEach(obj => {
      const objCorners = obj.getCoords()
      const edges = this.getEdges (objCorners)
      for (const targetCorner of targetCorners) {
        for (const edge of edges) {
          const closestPoint = this.closestPointOnSegment (targetCorner, edge.start, edge.end)
          const distance = this.getDistance (targetCorner, closestPoint)
          if (distance < minDistanceEdge) {
            minDistanceEdge = distance
            closestEdge = obj
          }
        }
      }
    })
    // console.log('nearestBox result - closestEdge: ', (closestEdge) ? closestEdge.data.id : '')
    return closestEdge
  }

  private makeDropZone () {
    const dropZone = new fabric.Rect({
      originX: 'center',
      originY: 'center',
      left: -this.boxDef.width / 2 + 1,
      top: -this.boxDef.height / 2 + 1,
      width: this.boxDef.width,
      height: this.boxDef.height,
      fill: consts.vdzFill,
      opacity: 0, // consts.vdzOpacity,
      name: 'virtualdrop',
      selectable: false
    })
    this.canvas.add(dropZone)
    this.canvas.moveTo(dropZone, consts.zforVirtual)
    return dropZone
  }

  private getDistance(p1: Point, p2: Point): number {
    const dx = p2.x - p1.x
    const dy = p2.y - p1.y
    return Math.sqrt(dx * dx + dy * dy)
  }

  private getEdges(corners: Point[]): Array<{start: Point, end: Point}> {
    return [
      { start: corners[0], end: corners[1] },
      { start: corners[1], end: corners[2] },
      { start: corners[2], end: corners[3] },
      { start: corners[3], end: corners[0] }
    ]
  }

  private closestPointOnSegment(point: Point, lineStart: Point, lineEnd: Point): Point {
    const dx = lineEnd.x - lineStart.x
    const dy = lineEnd.y - lineStart.y
    if (dx === 0 && dy === 0) { return lineStart }
    const t = Math.max(0,
      Math.min(1, ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / (dx * dx + dy * dy)))
    return {
      x: lineStart.x + t * dx,
      y: lineStart.y + t * dy
    }
  }
}
